// src/pages/ShippingPage.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Truck, Package, MapPin, Clock, CheckCircle, AlertCircle
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useSearch } from '../context/SearchContext';
import { openCartSidebar } from '../components/CartSidebar';

export default function ShippingPage() {
  const navigate = useNavigate();
  const { cartCount } = useCart();
  const { wishlistCount } = useWishlist();
  const { searchQuery, setSearchQuery } = useSearch();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <div className="shipping-page">
      <Style />
      
      <Header
        wishlistCount={wishlistCount}
        cartCount={cartCount}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onSearch={handleSearch}
        onCartClick={openCartSidebar}
        onWishlistClick={() => navigate('/wishlist')}
        onCampaignsClick={() => navigate('/campaigns')}
      />

      <div className="page-hero">
        <div className="container">
          <div className="hero-content">
            <Truck size={48} className="hero-icon" />
            <h1>Teslimat Bilgileri</h1>
            <p>Hızlı ve güvenilir kargo hizmetimiz hakkında detaylı bilgiler</p>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="container">
          {/* Highlights */}
          <div className="highlights-grid">
            <div className="highlight-card">
              <div className="highlight-icon">
                <Clock size={32} />
              </div>
              <h3>Hızlı Teslimat</h3>
              <p>1-3 iş günü içinde kargoda</p>
            </div>
            <div className="highlight-card">
              <div className="highlight-icon">
                <MapPin size={32} />
              </div>
              <h3>Türkiye Geneli</h3>
              <p>Her ile teslimat yapıyoruz</p>
            </div>
            <div className="highlight-card">
              <div className="highlight-icon">
                <CheckCircle size={32} />
              </div>
              <h3>Güvenli Paketleme</h3>
              <p>Özenle paketlenmiş ürünler</p>
            </div>
          </div>

          {/* Main Content */}
          <div className="content-grid">
            {/* Kargo Ücretleri */}
            <div className="content-section">
              <div className="section-header">
                <Package size={24} />
                <h2>Kargo Ücretleri</h2>
              </div>
              <div className="info-box">
                <div className="info-item">
                  <AlertCircle size={20} className="alert-icon" />
                  <div>
                    <strong>Kargo ücreti ürünlere göre farklılık göstermektedir.</strong>
                  </div>
                </div>
              </div>
            </div>

            {/* Teslimat Süreleri */}
            <div className="content-section">
              <div className="section-header">
                <Clock size={24} />
                <h2>Teslimat Süreleri</h2>
              </div>
              <div className="timeline">
                <div className="timeline-item">
                  <div className="timeline-dot"></div>
                  <div className="timeline-content">
                    <h4>Sipariş Onayı</h4>
                    <p>Siparişiniz onaylandıktan sonra hemen hazırlığa alınır</p>
                    <span className="time">Anında</span>
                  </div>
                </div>
                <div className="timeline-item">
                  <div className="timeline-dot"></div>
                  <div className="timeline-content">
                    <h4>Kargoya Teslim</h4>
                    <p>Ürünleriniz özenle paketlenir ve kargoya verilir</p>
                    <span className="time">1-3 İş Günü</span>
                  </div>
                </div>
                <div className="timeline-item">
                  <div className="timeline-dot"></div>
                  <div className="timeline-content">
                    <h4>Teslimat</h4>
                    <p>Kargo firması ürününüzü adresinize teslim eder</p>
                    <span className="time">1-3 İş Günü</span>
                  </div>
                </div>
              </div>
              <div className="note-box">
                <strong>Not:</strong> Resmi tatiller ve yoğun dönemlerde teslimat süreleri uzayabilir.
              </div>
            </div>

            {/* Kargo Firmaları */}
            <div className="content-section">
              <div className="section-header">
                <Truck size={24} />
                <h2>Kargo Firmaları</h2>
              </div>
              <p className="section-desc">
                Güvenilir kargo firmalarıyla çalışarak ürünlerinizin size güvenli ve hızlı ulaşmasını sağlıyoruz.
              </p>
              <div className="cargo-list">
                <div className="cargo-item">
                  <div className="cargo-name">Sürat Kargo</div>
                  <div className="cargo-badge">Aktif</div>
                </div>
              </div>
            </div>

            {/* Kargo Takibi */}
            <div className="content-section">
              <div className="section-header">
                <MapPin size={24} />
                <h2>Kargo Takibi</h2>
              </div>
              <div className="tracking-steps">
                <div className="step">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <h4>Takip Numaranızı Alın</h4>
                    <p>Siparişiniz kargoya verildikten sonra SMS ve e-posta ile takip numaranız tarafınıza iletilir.</p>
                  </div>
                </div>
                <div className="step">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <h4>Hesabınızdan Takip Edin</h4>
                    <p>"Siparişlerim" sayfasından tüm siparişlerinizi ve kargo durumlarını görebilirsiniz.</p>
                  </div>
                </div>
                <div className="step">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <h4>Kargo Firmasından Takip</h4>
                    <p>Takip numaranız ile kargo firmasının web sitesinden anlık konum bilgisi alabilirsiniz.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Teslimat Noktaları */}
            <div className="content-section">
              <div className="section-header">
                <MapPin size={24} />
                <h2>Teslimat Noktaları</h2>
              </div>
              <div className="delivery-options">
                <div className="delivery-option">
                  <CheckCircle size={20} />
                  <div>
                    <h4>Adresinize Teslimat</h4>
                    <p>Ürününüz sipariş sırasında belirttiğiniz adrese teslim edilir.</p>
                  </div>
                </div>
                <div className="delivery-option">
                  <CheckCircle size={20} />
                  <div>
                    <h4>Kargo Şubesinden Alım</h4>
                    <p>Teslimat yapılamazsa ürününüzü size en yakın kargo şubesinden alabilirsiniz.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Önemli Bilgiler */}
            <div className="content-section important">
              <div className="section-header">
                <AlertCircle size={24} />
                <h2>Önemli Bilgiler</h2>
              </div>
              <ul className="important-list">
                <li>Teslimat sırasında mutlaka kimlik kontrolü yapılır.</li>
                <li>Ürünü teslim almadan önce paketin hasarlı olup olmadığını kontrol edin.</li>
                <li>Hasarlı paketleri teslim almayın ve durumu kargo görevlisine bildirin.</li>
                <li>Yurtdışı teslimat şu an için mevcut değildir.</li>
                <li>Adres bilgilerinizi eksiksiz ve doğru girdiğinizden emin olun.</li>
                <li>Telefon numaranızı mutlaka güncel tutun, kargo firması sizi arayabilir.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

function Style() {
  return (
    <style>{`
      * { margin: 0; padding: 0; box-sizing: border-box; }
      
      .shipping-page {
        min-height: 100vh;
        background: #f8f9fa;
      }

      .container {
        max-width: 1320px;
        margin: 0 auto;
        padding: 0 20px;
      }

      .page-hero {
        background: linear-gradient(135deg, #2B2B2B 0%, #1a1a1a 100%);
        padding: 80px 0;
        position: relative;
        overflow: hidden;
      }

      .page-hero::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: radial-gradient(circle at 20% 50%, rgba(229, 51, 42, 0.1), transparent 50%);
      }

      .hero-content {
        text-align: center;
        color: #fff;
        position: relative;
        z-index: 1;
      }

      .hero-icon {
        margin-bottom: 24px;
        opacity: 0.9;
      }

      .hero-content h1 {
        font-size: 48px;
        font-weight: 800;
        margin-bottom: 16px;
      }

      .hero-content p {
        font-size: 18px;
        opacity: 0.8;
      }

      .page-content {
        padding: 60px 0;
      }

      /* Highlights Grid */
      .highlights-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 24px;
        margin-bottom: 60px;
      }

      .highlight-card {
        background: #fff;
        padding: 32px;
        border-radius: 16px;
        text-align: center;
        border: 2px solid #e9ecef;
        transition: all 0.3s;
      }

      .highlight-card:hover {
        border-color: #E5332A;
        transform: translateY(-4px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
      }

      .highlight-icon {
        width: 64px;
        height: 64px;
        background: linear-gradient(135deg, #E5332A, #ff4436);
        border-radius: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        margin: 0 auto 16px;
      }

      .highlight-card h3 {
        font-size: 18px;
        margin-bottom: 8px;
        color: #2B2B2B;
      }

      .highlight-card p {
        font-size: 14px;
        color: #868e96;
      }

      /* Content Grid */
      .content-grid {
        max-width: 900px;
        margin: 0 auto;
        display: flex;
        flex-direction: column;
        gap: 32px;
      }

      .content-section {
        background: #fff;
        padding: 40px;
        border-radius: 16px;
        border: 2px solid #e9ecef;
      }

      .content-section.important {
        border-color: #E5332A;
        background: #fff5f5;
      }

      .section-header {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 24px;
        color: #E5332A;
      }

      .section-header h2 {
        font-size: 24px;
        font-weight: 700;
        color: #2B2B2B;
      }

      .section-desc {
        color: #495057;
        line-height: 1.7;
        margin-bottom: 24px;
      }

      /* Info Box */
      .info-box {
        display: flex;
        flex-direction: column;
        gap: 16px;
        margin-bottom: 20px;
      }

      .info-item {
        display: flex;
        gap: 16px;
        padding: 20px;
        background: #f8f9fa;
        border-radius: 12px;
      }

      .check-icon {
        color: #06d6a0;
        flex-shrink: 0;
      }

      .alert-icon {
        color: #E5332A;
        flex-shrink: 0;
      }

      .info-item strong {
        display: block;
        color: #2B2B2B;
        margin-bottom: 4px;
      }

      .info-item p {
        color: #495057;
        font-size: 14px;
      }

      /* Tip Box */
      .tip-box, .note-box {
        padding: 16px 20px;
        background: #fff9e6;
        border-left: 4px solid #f39c12;
        border-radius: 8px;
        color: #495057;
        font-size: 14px;
      }

      .note-box {
        background: #e7f5ff;
        border-left-color: #3498db;
        margin-top: 20px;
      }

      /* Timeline */
      .timeline {
        position: relative;
        padding-left: 40px;
      }

      .timeline::before {
        content: '';
        position: absolute;
        left: 15px;
        top: 8px;
        bottom: 8px;
        width: 2px;
        background: #e9ecef;
      }

      .timeline-item {
        position: relative;
        margin-bottom: 32px;
      }

      .timeline-item:last-child {
        margin-bottom: 0;
      }

      .timeline-dot {
        position: absolute;
        left: -32px;
        top: 0;
        width: 16px;
        height: 16px;
        background: #E5332A;
        border-radius: 50%;
        border: 3px solid #fff;
        box-shadow: 0 0 0 2px #e9ecef;
      }

      .timeline-content h4 {
        font-size: 16px;
        margin-bottom: 8px;
        color: #2B2B2B;
      }

      .timeline-content p {
        color: #495057;
        font-size: 14px;
        margin-bottom: 8px;
      }

      .time {
        display: inline-block;
        padding: 4px 12px;
        background: #E5332A;
        color: #fff;
        border-radius: 6px;
        font-size: 12px;
        font-weight: 600;
      }

      /* Cargo List */
      .cargo-list {
        display: grid;
        gap: 12px;
      }

      .cargo-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px 20px;
        background: #f8f9fa;
        border-radius: 12px;
        border: 2px solid #e9ecef;
      }

      .cargo-name {
        font-weight: 600;
        color: #2B2B2B;
      }

      .cargo-badge {
        padding: 4px 12px;
        background: #06d6a0;
        color: #fff;
        border-radius: 6px;
        font-size: 12px;
        font-weight: 600;
      }

      /* Tracking Steps */
      .tracking-steps {
        display: flex;
        flex-direction: column;
        gap: 24px;
      }

      .step {
        display: flex;
        gap: 20px;
      }

      .step-number {
        width: 40px;
        height: 40px;
        background: linear-gradient(135deg, #E5332A, #ff4436);
        color: #fff;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        flex-shrink: 0;
      }

      .step-content h4 {
        font-size: 16px;
        margin-bottom: 8px;
        color: #2B2B2B;
      }

      .step-content p {
        color: #495057;
        font-size: 14px;
        line-height: 1.6;
      }

      /* Delivery Options */
      .delivery-options {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      .delivery-option {
        display: flex;
        gap: 16px;
        padding: 20px;
        background: #f8f9fa;
        border-radius: 12px;
      }

      .delivery-option svg {
        color: #06d6a0;
        flex-shrink: 0;
      }

      .delivery-option h4 {
        font-size: 16px;
        margin-bottom: 4px;
        color: #2B2B2B;
      }

      .delivery-option p {
        color: #495057;
        font-size: 14px;
      }

      /* Important List */
      .important-list {
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .important-list li {
        padding-left: 28px;
        position: relative;
        color: #495057;
        line-height: 1.6;
      }

      .important-list li::before {
        content: '⚠️';
        position: absolute;
        left: 0;
      }

      /* Responsive */
      @media (max-width: 992px) {
        .highlights-grid {
          grid-template-columns: repeat(2, 1fr);
        }
      }

      @media (max-width: 768px) {
        .page-hero {
          padding: 60px 0;
        }

        .hero-content h1 {
          font-size: 32px;
        }

        .highlights-grid {
          grid-template-columns: 1fr;
        }

        .content-section {
          padding: 24px;
        }

        .timeline {
          padding-left: 32px;
        }

        .step {
          flex-direction: column;
        }
      }
    `}</style>
  );
}